import React, { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

import {
  acquireAccessToken,
  getCurrentAccessToken,
  isInteractiveTokenAcquisitionStartedError,
  subscribeToAccessTokenChanged
} from 'services/tokenService';

import { permissionCodeMap, permissions as appPermissions } from 'utils/permissions';

export type PermissionMode = 'all' | 'any';

export type PermissionLoadState = 'loading' | 'loaded' | 'unauthenticated' | 'error';

type PermissionContextType = {
  loadState: PermissionLoadState;
  isLoaded: boolean;
  hasPermission: (permissions?: string | string[], mode?: PermissionMode) => boolean;
};

export const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

type JwtClaims = {
  transUniPermissions?: string;
  p?: string;
  permissions?: string;
};

function decodePermissionClaim(claim: string): string[] {
  return claim
    .split(',')
    .map((permission) => permission.trim())
    .filter(Boolean)
    .map((permission) => {
      const code = Number.parseInt(permission, 10);

      return !Number.isNaN(code) && code in permissionCodeMap ? permissionCodeMap[code] : permission;
    });
}

function derivePermissions(permissions: string[]): string[] {
  const permissionSet = new Set(permissions);
  const hasOwnerOrAssistantLevelPermissions =
    permissionSet.has(appPermissions.user.USER_LIST) &&
    permissionSet.has(appPermissions.user.USER_CREATE) &&
    permissionSet.has(appPermissions.role.ROLE_LIST) &&
    permissionSet.has(appPermissions.project.PROJECT_DELETE) &&
    permissionSet.has(appPermissions.worker.WORKER_DELETE);

  if (!hasOwnerOrAssistantLevelPermissions) {
    return permissions;
  }

  return [
    ...permissions,
    appPermissions.talentPool.TALENT_POOL_LIST,
    appPermissions.talentPool.TALENT_POOL_READ,
    appPermissions.talentPool.TALENT_POOL_CREATE,
    appPermissions.talentPool.TALENT_POOL_UPDATE,
    appPermissions.talentPool.TALENT_POOL_DELETE
  ];
}

function extractPermissions(jwtToken: string): string[] {
  const decoded = jwtDecode<JwtClaims>(jwtToken);
  let extractedPermissions: string[] = [];

  if (decoded.transUniPermissions) {
    extractedPermissions = decodePermissionClaim(decoded.transUniPermissions);
  } else if (decoded.p) {
    extractedPermissions = decodePermissionClaim(decoded.p);
  } else if (decoded.permissions) {
    extractedPermissions = decodePermissionClaim(decoded.permissions);
  }

  return Array.from(new Set(derivePermissions(extractedPermissions)));
}

export function PermissionProvider({ children }: Readonly<PropsWithChildren>) {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loadState, setLoadState] = useState<PermissionLoadState>('loading');

  const applyToken = useCallback((accessToken: string | null) => {
    if (!accessToken) {
      setPermissions([]);
      setLoadState('unauthenticated');
      return;
    }

    try {
      const extractedPermissions = extractPermissions(accessToken);

      setPermissions(extractedPermissions);
      setLoadState('loaded');
    } catch (error) {
      console.error('Failed to extract permissions from access token.', error);

      setPermissions([]);
      setLoadState('error');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = subscribeToAccessTokenChanged((accessToken) => {
      if (!cancelled) {
        applyToken(accessToken);
      }
    });

    const currentAccessToken = getCurrentAccessToken();

    if (currentAccessToken) {
      applyToken(currentAccessToken);
    }

    acquireAccessToken().catch((error) => {
      if (cancelled) {
        return;
      }

      if (isInteractiveTokenAcquisitionStartedError(error)) {
        setLoadState('loading');
        return;
      }

      console.error('Initial access token acquisition failed.', error);

      setPermissions([]);
      setLoadState('error');
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [applyToken]);

  const permissionSet = useMemo(() => new Set(permissions), [permissions]);

  const hasPermission = useCallback(
    (requiredPermissions?: string | string[], mode: PermissionMode = 'all') => {
      if (!requiredPermissions) {
        return true;
      }

      const requiredPermissionArray = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];

      if (requiredPermissionArray.length === 0) {
        return true;
      }

      if (mode === 'any') {
        return requiredPermissionArray.some((permission) => permissionSet.has(permission));
      }

      return requiredPermissionArray.every((permission) => permissionSet.has(permission));
    },
    [permissionSet]
  );

  const value = useMemo<PermissionContextType>(
    () => ({
      loadState,
      isLoaded: loadState === 'loaded',
      hasPermission
    }),
    [loadState, hasPermission]
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}
